import type { MailScheduledSendEntry } from "@/lib/api/mailScheduledSend";

const cache = new Map<number, MailScheduledSendEntry[]>();
const listeners = new Set<() => void>();

function notify(): void {
  for (const fn of listeners) {
    fn();
  }
}

export function subscribeScheduledSendsCacheChanged(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function getScheduledSendsCache(accountId: number): MailScheduledSendEntry[] | null {
  const rows = cache.get(accountId);
  return rows ? [...rows] : null;
}

function sortScheduledEntries(entries: MailScheduledSendEntry[]): MailScheduledSendEntry[] {
  return [...entries].sort((a, b) => {
    const ta = new Date(a.sendAt).getTime();
    const tb = new Date(b.sendAt).getTime();
    if (ta !== tb) return ta - tb;
    return a.id - b.id;
  });
}

export function setScheduledSendsCache(
  accountId: number,
  entries: MailScheduledSendEntry[]
): void {
  cache.set(accountId, entries);
  notify();
}

/**
 * Applique la liste serveur en préservant :
 * - placeholders locaux (id &lt; 1) encore présents → spinner programmation
 * - entrées réelles ajoutées pendant le fetch (POST terminé, GET encore stale)
 */
export function applyScheduledSendsFromServer(
  accountId: number,
  serverEntries: MailScheduledSendEntry[],
  snapshotBeforeFetch: MailScheduledSendEntry[] = []
): MailScheduledSendEntry[] {
  const idsAtStart = new Set(snapshotBeforeFetch.map((row) => row.id));
  const afterLocal = cache.get(accountId) ?? [];
  const placeholdersNow = afterLocal.filter((row) => row.id < 1);
  const serverIds = new Set(serverEntries.map((row) => row.id));
  const addedDuringFetch = afterLocal.filter(
    (row) => row.id > 0 && !serverIds.has(row.id) && !idsAtStart.has(row.id)
  );
  const next = sortScheduledEntries([
    ...placeholdersNow,
    ...addedDuringFetch,
    ...serverEntries,
  ]);
  setScheduledSendsCache(accountId, next);
  return next;
}

/** Ajoute ou remplace un envoi programmé (réponse POST) — affichage instantané. */
export function upsertScheduledSendCache(
  accountId: number,
  entry: MailScheduledSendEntry
): void {
  const prev = cache.get(accountId) ?? [];
  const index = prev.findIndex((row) => row.id === entry.id);
  const next =
    index >= 0
      ? prev.map((row, i) => (i === index ? entry : row))
      : [...prev, entry];
  setScheduledSendsCache(accountId, sortScheduledEntries(next));
}

export function removeScheduledSendPlaceholders(accountId: number): void {
  const prev = cache.get(accountId);
  if (!prev?.length) return;
  const next = prev.filter((row) => row.id > 0);
  if (next.length === prev.length) return;
  setScheduledSendsCache(accountId, next);
}

export function getScheduledSendsCachedCount(accountId: number): number {
  return cache.get(accountId)?.length ?? 0;
}

export function clearScheduledSendsCache(accountId?: number): void {
  if (accountId == null) {
    cache.clear();
  } else {
    cache.delete(accountId);
  }
  notify();
}

